import { useState } from 'react';
import { MdHub, MdOutlineDashboard, MdOutlineTune } from 'react-icons/md';
import { useHistory } from 'react-router-dom';
import { Button, Tooltip, Whisper } from 'rsuite';
import bellIcon from '../../../../../../assets/icons/icon_bell_white.svg';
import binocularsIcon from '../../../../../../assets/icons/icon_binoculars_blue.svg';
import { ModalSpeakWithSpecialist, type ModalSpeakWithSpecialistFeature } from '../../../../../../components/ModalSpeakWithSpecialist';
import { trackEvent } from '../../../../../../utils/MatomoConfig';
import { INFOPANEL_MONITORAMENTO_CTA_LINK } from '../../../../../../utils/constants';
import { sendLead } from '../../../InfoPanel/utils';
import {
  type AnaliseCompetitividadePreferences,
  type AnaliseIndustriaFiltros,
  type AnaliseVarejoFiltros,
  mapAnaliseCompetitividadeToMonitoramento,
} from '../../../CentralDeMonitoramento/analiseCompetitividadeMapper';
import { CriarMonitoramentoModal } from '../../../CentralDeMonitoramento/Components';
import { MonitoramentoDetalheDTO, MonitoramentoTipo } from '../../../CentralDeMonitoramento/types';
import styles from './MonitorButton.module.scss';

const BLOCKED_MODAL_SUBTITLE =
  'Fique sempre um passo à frente monitorando mudanças de preço e estratégias dos seus concorrentes de acordo com ' +
  'critérios pré-estabelecidos e as suas necessidades.';

const BLOCKED_MODAL_FEATURES: ModalSpeakWithSpecialistFeature[] = [
  {
    icon: MdOutlineTune,
    title: 'Parâmetros sob medida',
    description: 'Escolha o que comparar, o local e o período que fizerem sentido pra sua análise.',
  },
  {
    icon: MdOutlineDashboard,
    title: 'Central de Monitoramento',
    description: 'Veja em um só lugar tudo o que está fora dos parâmetros definidos por você.',
  },
  {
    icon: MdHub,
    title: 'Crie diferentes monitoramentos',
    description: 'Crie monitoramentos com critérios distintos para cada necessidade de análise.',
  },
];

const BUTTON_TOOLTIP_TEXT =
  'Tenha controle da sua competitividade recebendo alertas quando houver variações de acordo com critérios pré-estabelecidos.';

type MonitorButtonProps = {
  visible: boolean;
  tipo: MonitoramentoTipo;
  filtros: AnaliseVarejoFiltros | AnaliseIndustriaFiltros;
  preferences: AnaliseCompetitividadePreferences;
  hasModuloContratado: boolean;
  usuario: Record<string, unknown>;
};

export const MonitorButton = ({ visible, tipo, filtros, preferences, hasModuloContratado, usuario }: MonitorButtonProps) => {
  const history = useHistory();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false);

  if (!visible) return null;

  const handleClick = () => {
    if (!hasModuloContratado) {
      setIsBlockedModalOpen(true);
      return;
    }
    setIsCreateModalOpen(true);
  };

  // A checagem de duplicidade é feita pelo back no próprio POST de criação, dentro da modal
  // (mesmo comportamento que a página cheia de Criar Monitoramento já tem hoje) — não há checagem
  // prévia de existência aqui, o botão fica sempre habilitado.
  const handleCreated = (_detalhe: MonitoramentoDetalheDTO) => {
    setIsCreateModalOpen(false);
    history.push('/isa/central-de-monitoramento');
  };

  const handleContratar = () => {
    sendLead(usuario, 'upsell-monitoramento-competitividade');
    trackEvent('Upsell/Crossell', 'upsell-block-monitoramento-competitividade');
    setIsBlockedModalOpen(false);
    window.open(INFOPANEL_MONITORAMENTO_CTA_LINK);
  };

  return (
    <>
      <div className={styles.row}>
        <Whisper trigger="hover" placement="top" speaker={<Tooltip>{BUTTON_TOOLTIP_TEXT}</Tooltip>}>
          <Button id="monitorar-competitividade-button" appearance="ghost" className={styles.button} onClick={handleClick}>
            <img src={binocularsIcon} alt="" className={styles.icon} />
            MONITORAR COMPETITIVIDADE
          </Button>
        </Whisper>
      </div>

      {isCreateModalOpen && (
        <CriarMonitoramentoModal
          initialValues={mapAnaliseCompetitividadeToMonitoramento(tipo, filtros, preferences)}
          onClose={() => setIsCreateModalOpen(false)}
          onCreated={handleCreated}
        />
      )}

      <ModalSpeakWithSpecialist
        show={isBlockedModalOpen}
        title="Controle a sua competitividade recebendo alertas dos seus concorrentes."
        subtitle={BLOCKED_MODAL_SUBTITLE}
        secondaryTitle="O monitoramento de alertas também inclui:"
        features={BLOCKED_MODAL_FEATURES}
        actionLabel="Contratar Monitoramento"
        icon={bellIcon}
        onClick={handleContratar}
        onCancel={() => setIsBlockedModalOpen(false)}
      />
    </>
  );
};
